/**
 * OBJECTS-section annotative parsers (PR-2 of the annotation-scale parity
 * plan).
 *
 * AutoCAD persists per-annotation-scale data in the OBJECTS section as
 * a closed family of `AcDb*ObjectContextData` objects, one per
 * annotative entity per active annotation scale. The catalogue of named
 * scales themselves lives in `AcDbScale` records owned by the
 * `ACAD_SCALELIST` dictionary. This module provides the typed parsers
 * for both kinds and the dispatcher used by `parseObjectsSection`.
 *
 * Reference: AutoCAD ObjectARX 2018 reference, "AcDbObjectContextData"
 * class hierarchy.
 *
 * ## On-disk shape
 *
 * `AcDbScale` (catalogue entry):
 * ```
 * 0   SCALE
 * 5   <handle>
 * 330 <ownerHandle>             ← ACAD_SCALELIST dict handle
 * 100 AcDbScale
 * 70  <flags>
 * 300 <name>                    ← e.g. "1:50"
 * 140 <paperUnits>              ← double
 * 141 <drawingUnits>            ← double
 * 290 <isUnitScale 0|1>
 * ```
 * scaleFactor = paperUnits / drawingUnits.
 *
 * `AcDb*ObjectContextData` (one record per entity per scale):
 * ```
 * 0   <subclass-specific entity-type>  e.g. ACDBPERSSUBENTMANAGER, or
 *                                        a vendor-extended type. AutoCAD
 *                                        emits the raw subclass name as
 *                                        the type for newer records — we
 *                                        match by the 100 markers below.
 * 5   <handle>
 * 330 <ownerHandle>             ← typically the per-entity child DICTIONARY
 * 100 AcDbObjectContextData     ← base subclass (group 70 = flags)
 * 70  <flags>
 * 100 AcDb<Concrete>ObjectContextData  ← concrete kind marker
 * 340 <scaleHandle>             ← FK into ACAD_SCALELIST → AcDbScale
 * <subclass-specific groups>
 * ```
 *
 * Subclass-specific groups vary per kind (TEXT carries 10/20 + 11/21 +
 * 50; INSERT carries 10/20 + 41/42 + 50; HATCH carries 41/53; etc.).
 *
 * ## Why this lives in its own module
 *
 * `sectionParsers.ts` is already 1341 lines and approaching the 2000-line
 * hard limit. The annotative dispatcher + ten subclass parsers is large
 * enough (~600 LoC) that hosting it inline would push sectionParsers
 * past the limit. Per CLAUDE.md priority #2 + file-size rules, this lives
 * in `packages/dxf-io/src/objects/objectsAnnotativeParser.ts`.
 *
 * ## Resolution flow
 *
 * 1. **First pass (this module)** — recognise an OBJECTS record by its
 *    100-subclass markers, decode all known group codes, capture
 *    unknown group codes in `rawGroups` for round-trip, and emit a
 *    typed `ParsedAnnotativeRecord`. The `scaleHandle` is left as a
 *    string at this stage; the actual scale name + factor are resolved
 *    in the second pass.
 *
 * 2. **Second pass (handleResolution.resolveAnnotativeContexts)** —
 *    builds Map<scaleHandle → AcDbScale>, walks every entity that has
 *    an `extensionDictionaryHandle`, finds the chained context-data
 *    records, and populates `entity.annotativeContexts[]`.
 *
 * Streaming preservation: this module operates on already-extracted
 * group lists (one record at a time) so it composes seamlessly with
 * the streaming parser — no eager-load of the whole file is introduced.
 */

import type { DxfGroup } from "../types";
import { parseFloatSafe, parseIntSafe } from "./groupCodec";
import { decodeDxfUnicode } from "../dxfUnicodeEscape";

// Annotative type vocabulary lives in `objectsAnnotativeTypes.ts` so
// `types.ts` can pull these record shapes in without re-importing this
// parser (was a depcruise cycle source). Re-exported below for
// ergonomic back-compat with downstream consumers.
export type {
  AnnotScalePoint,
  AnnotScaleBase,
  AnnotScaleTextContext,
  AnnotScaleMtextContext,
  AnnotScaleMtextAttributeContext,
  AnnotScaleAttributeContext,
  AnnotScaleBlockrefContext,
  AnnotScaleHatchContext,
  AnnotScaleDimensionSubtype,
  AnnotScaleDimensionContext,
  AnnotScaleMleaderContext,
  AnnotScaleLeaderContext,
  AnnotScaleToleranceContext,
  AnnotScaleContext,
  AcDbScale,
  AcadScaleListDictEntry,
  ParsedAnnotativeRecord,
} from "./objectsAnnotativeTypes";

import type {
  AnnotScalePoint,
  AnnotScaleBase,
  AnnotScaleTextContext,
  AnnotScaleMtextContext,
  AnnotScaleMtextAttributeContext,
  AnnotScaleAttributeContext,
  AnnotScaleBlockrefContext,
  AnnotScaleHatchContext,
  AnnotScaleDimensionSubtype,
  AnnotScaleDimensionContext,
  AnnotScaleMleaderContext,
  AnnotScaleLeaderContext,
  AnnotScaleToleranceContext,
  AnnotScaleContext,
  AcDbScale,
  AcadScaleListDictEntry,
  ParsedAnnotativeRecord,
} from "./objectsAnnotativeTypes";

// ── Subclass markers ──────────────────────────────────────────────────

const SUBCLASS_TEXT = "AcDbTextObjectContextData";
const SUBCLASS_MTEXT = "AcDbMTextObjectContextData";
const SUBCLASS_MTEXT_ATTR = "AcDbMTextAttributeObjectContextData";
const SUBCLASS_BLKREF = "AcDbBlkRefObjectContextData";
const SUBCLASS_HATCH = "AcDbHatchScaleContextData";
const SUBCLASS_DIM = "AcDbDimensionObjectContextData";
const SUBCLASS_DIM_ALIGNED = "AcDbAlignedDimensionObjectContextData";
const SUBCLASS_DIM_LINEAR = "AcDbRotatedDimensionObjectContextData";
const SUBCLASS_DIM_ANGULAR_3PT = "AcDb3PointAngularDimensionObjectContextData";
const SUBCLASS_DIM_ANGULAR_2L = "AcDb2LineAngularDimensionObjectContextData";
const SUBCLASS_DIM_DIAM = "AcDbDiametricDimensionObjectContextData";
const SUBCLASS_DIM_RAD = "AcDbRadialDimensionObjectContextData";
const SUBCLASS_DIM_ORD = "AcDbOrdinateDimensionObjectContextData";
const SUBCLASS_MLEADER = "AcDbMLeaderObjectContextData";
const SUBCLASS_LEADER = "AcDbLeaderObjectContextData";
const SUBCLASS_FCF = "AcDbFcfObjectContextData";
const SUBCLASS_BASE = "AcDbObjectContextData";
// Intermediate base — every annotative context-data record carries this
// marker between `AcDbObjectContextData` and the concrete subclass. In
// some real-world AutoCAD files (e.g. АБИ1.dxf, the QA fixture) the
// concrete `AcDb<Kind>ObjectContextData` marker is OMITTED entirely from
// the OBJECTS section — only `AcDbObjectContextData` and
// `AcDbAnnotScaleObjectContextData` are present, and the concrete kind
// is encoded ONLY by the record's group-0 type (e.g. the registered
// CLASS name `ACDB_TEXTOBJECTCONTEXTDATA_CLASS`). We treat this marker
// as a member of the recognised set so detection still fires; the
// dispatcher then maps the type to a concrete subclass via
// TYPE_TO_SUBCLASS as a fallback.
const SUBCLASS_ANNOT_BASE = "AcDbAnnotScaleObjectContextData";

const ALL_CONTEXT_DATA_MARKERS = new Set<string>([
  SUBCLASS_TEXT,
  SUBCLASS_MTEXT,
  SUBCLASS_MTEXT_ATTR,
  SUBCLASS_BLKREF,
  SUBCLASS_HATCH,
  SUBCLASS_DIM,
  SUBCLASS_DIM_ALIGNED,
  SUBCLASS_DIM_LINEAR,
  SUBCLASS_DIM_ANGULAR_3PT,
  SUBCLASS_DIM_ANGULAR_2L,
  SUBCLASS_DIM_DIAM,
  SUBCLASS_DIM_RAD,
  SUBCLASS_DIM_ORD,
  SUBCLASS_MLEADER,
  SUBCLASS_LEADER,
  SUBCLASS_FCF,
  // Intermediate base — see comment on SUBCLASS_ANNOT_BASE for why.
  SUBCLASS_ANNOT_BASE,
]);

/**
 * Map from registered DXF class name (group 0 value of an OBJECTS-section
 * record) to the concrete annotative-subclass marker. Used as a fallback
 * by `dispatchObjectsAnnotativeRecord` when a record carries only the
 * intermediate `AcDbAnnotScaleObjectContextData` marker without the
 * concrete `AcDb<Kind>ObjectContextData` marker (observed in АБИ1.dxf:
 * 111 records, 0 concrete markers — concrete kind encoded ONLY in group
 * 0). The class names match the entries the writer emits in the CLASSES
 * section for the same kinds.
 *
 * Keys are upper-case (we upper-case the input before lookup).
 */
const TYPE_TO_SUBCLASS: Readonly<Record<string, string>> = {
  ACDB_TEXTOBJECTCONTEXTDATA_CLASS: SUBCLASS_TEXT,
  ACDB_MTEXTOBJECTCONTEXTDATA_CLASS: SUBCLASS_MTEXT,
  ACDB_MTEXTATTRIBUTEOBJECTCONTEXTDATA_CLASS: SUBCLASS_MTEXT_ATTR,
  ACDB_BLKREFOBJECTCONTEXTDATA_CLASS: SUBCLASS_BLKREF,
  ACDB_HATCHSCALECONTEXTDATA_CLASS: SUBCLASS_HATCH,
  ACDB_DIMENSIONOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM,
  ACDB_ALDIMOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_ALIGNED,
  ACDB_ALIGNEDDIMENSIONOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_ALIGNED,
  ACDB_RDIMOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_LINEAR,
  ACDB_ROTATEDDIMENSIONOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_LINEAR,
  ACDB_ANG3PTDIMOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_ANGULAR_3PT,
  ACDB_3POINTANGULARDIMENSIONOBJECTCONTEXTDATA_CLASS:
    SUBCLASS_DIM_ANGULAR_3PT,
  ACDB_ANG2LDIMOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_ANGULAR_2L,
  ACDB_2LINEANGULARDIMENSIONOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_ANGULAR_2L,
  ACDB_DMDIMOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_DIAM,
  ACDB_DIAMETRICDIMENSIONOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_DIAM,
  ACDB_RADIMOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_RAD,
  ACDB_RADIALDIMENSIONOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_RAD,
  ACDB_ORDDIMOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_ORD,
  ACDB_ORDINATEDIMENSIONOBJECTCONTEXTDATA_CLASS: SUBCLASS_DIM_ORD,
  ACDB_MLEADEROBJECTCONTEXTDATA_CLASS: SUBCLASS_MLEADER,
  ACDB_LEADEROBJECTCONTEXTDATA_CLASS: SUBCLASS_LEADER,
  ACDB_FCFOBJECTCONTEXTDATA_CLASS: SUBCLASS_FCF,
};

// ── Helpers ────────────────────────────────────────────────────────────
//
// The DXF group-value codec (parseFloatSafe / parseIntSafe) lives in the
// shared `groupCodec` leaf module — see the import above. Formerly local
// copies; consolidated to one truth across the objects/ parsers + writers.

/**
 * Identify whether a parsed object record should be treated as an
 * `AcDbScale` catalogue entry. Examines both the record type (group 0)
 * and the 100-subclass marker chain (the latter is authoritative because
 * AutoCAD emits "SCALE" as the type but `AcDbScale` only as a subclass).
 */
export function isAcDbScaleType(type: string, groups: DxfGroup[]): boolean {
  if (type === "SCALE") return true;
  for (const g of groups) {
    if (g.code === 100 && g.value.trim() === "AcDbScale") return true;
  }
  return false;
}

/**
 * Identify whether a parsed object record should be treated as one of
 * the per-scale context-data records. Walks the 100 subclass marker
 * sequence — the AutoCAD object type (group 0) is unstable across
 * versions but the marker chain is canonical.
 */
export function isAnnotScaleContextDataType(
  _type: string,
  groups: DxfGroup[],
): boolean {
  for (const g of groups) {
    if (g.code !== 100) continue;
    const marker = g.value.trim();
    if (ALL_CONTEXT_DATA_MARKERS.has(marker)) return true;
  }
  return false;
}

// ── parseAcDbScale ─────────────────────────────────────────────────────

/**
 * Decode a single `AcDbScale` catalogue entry from its raw group list.
 *
 * Group codes (per Autodesk DXF reference for AcDbScale):
 *   5   → handle
 *   330 → ownerHandle (parent ACAD_SCALELIST dictionary)
 *   100 → subclass markers ("AcDbObject", "AcDbScale")
 *   70  → flags
 *   300 → name
 *   140 → paperUnits (double)
 *   141 → drawingUnits (double)
 *   290 → isUnitScale (0/1)
 *
 * scaleFactor is computed as paperUnits / drawingUnits with a guard
 * against division by zero (degenerate AcDbScale records seen in the
 * wild — e.g. autosaved drafts — fall back to 1.0).
 */
export function parseAcDbScale(groups: DxfGroup[]): AcDbScale {
  const scale: AcDbScale = {
    handle: "",
    name: "",
    scaleFactor: 1,
    paperUnits: 1,
    drawingUnits: 1,
    isUnitScale: false,
    flags: 0,
    isHidden: false,
  };

  for (const g of groups) {
    switch (g.code) {
      case 5:
        scale.handle = g.value.trim();
        break;
      case 330:
        scale.ownerHandle = g.value.trim();
        break;
      case 70:
        scale.flags = parseIntSafe(g.value);
        break;
      case 300:
        scale.name = decodeDxfUnicode(g.value); // T9 PR-3 — AcDbScale name decode
        break;
      case 140:
        scale.paperUnits = parseFloatSafe(g.value);
        break;
      case 141:
        scale.drawingUnits = parseFloatSafe(g.value);
        break;
      case 290:
        scale.isUnitScale = parseIntSafe(g.value) !== 0;
        break;
      default:
        // Unknown / vendor-specific group — silently ignored at the
        // catalogue level (the OBJECTS-section round-trip path keeps
        // SCALE records verbatim through rawObjectGroups already).
        break;
    }
  }

  if (scale.drawingUnits !== 0) {
    scale.scaleFactor = scale.paperUnits / scale.drawingUnits;
  }
  // Bit 0 of flags = isHidden per AutoCAD ObjectARX reference.
  scale.isHidden = (scale.flags & 1) !== 0;

  return scale;
}

// ── parseAcDbScaleListDict ────────────────────────────────────────────

/**
 * Parse the body of the `ACAD_SCALELIST` DICTIONARY into a flat ordered
 * list of (name, handle) pairs. Mirrors `parseDictionaryEntry` but kept
 * separate so the result type is the annotative-specific one used by
 * the resolver and the writer.
 *
 * Group codes:
 *   5   → handle of the dictionary itself
 *   330 → owner handle (the root NamedObjects DICTIONARY); the one OUTSIDE any
 *         `102 {…}` control block. A `330` INSIDE `102 {ACAD_REACTORS … 102 }`
 *         is a persistent-reactor reference, captured into `reactorHandles`.
 *   102 → control-block delimiter (`{ACAD_REACTORS` / `{ACAD_XDICTIONARY` / `}`).
 *   3   → entry name (e.g. "1:50")
 *   350 → soft pointer to the AcDbScale entry (preferred)
 *   360 → hard pointer to the AcDbScale entry (fallback)
 */
export function parseAcDbScaleListDict(
  groups: DxfGroup[],
): AcadScaleListDictEntry {
  const entry: AcadScaleListDictEntry = {
    handle: "",
    scales: [],
  };
  let pendingName: string | null = null;
  // Track the active `102 {NAME` control block so a `330` reactor reference
  // inside `{ACAD_REACTORS` is not mistaken for the record's own owner handle.
  let controlBlock: string | null = null;
  const reactorHandles: string[] = [];
  for (const g of groups) {
    switch (g.code) {
      case 5:
        entry.handle = g.value.trim();
        break;
      case 102: {
        const v = g.value.trim();
        if (v === "}") controlBlock = null;
        else if (v.startsWith("{")) controlBlock = v.slice(1);
        break;
      }
      case 330:
        if (controlBlock === "ACAD_REACTORS") {
          const h = g.value.trim();
          if (h.length > 0) reactorHandles.push(h);
        } else if (controlBlock === null) {
          entry.ownerHandle = g.value.trim();
        }
        break;
      case 3:
        pendingName = g.value.trim();
        break;
      case 350:
      case 360:
        if (pendingName !== null) {
          entry.scales.push({ name: pendingName, handle: g.value.trim() });
          pendingName = null;
        }
        break;
      default:
        break;
    }
  }
  if (reactorHandles.length > 0) entry.reactorHandles = reactorHandles;
  return entry;
}

// ── Subclass parsers ─────────────────────────────────────────────────

/**
 * Per-subclass parser type. Receives the body group list AFTER the
 * concrete subclass marker (everything between the marker and the next
 * 100/0 boundary, inclusive of unknown groups for `rawGroups`).
 */
type SubclassParser = (
  groups: DxfGroup[],
  base: AnnotScaleBase,
) => AnnotScaleContext;

const parseTextContext: SubclassParser = (groups, base) => {
  let ix: number | undefined;
  let iy: number | undefined;
  let ax: number | undefined;
  let ay: number | undefined;
  let rotation = 0;
  const rawGroups: DxfGroup[] = [];
  for (const g of groups) {
    switch (g.code) {
      case 10: ix = parseFloatSafe(g.value); break;
      case 20: iy = parseFloatSafe(g.value); break;
      case 11: ax = parseFloatSafe(g.value); break;
      case 21: ay = parseFloatSafe(g.value); break;
      case 50: rotation = parseFloatSafe(g.value); break;
      default: rawGroups.push(g);
    }
  }
  const ctx: AnnotScaleTextContext = {
    ...base,
    kind: "text",
    insertion: { x: ix ?? 0, y: iy ?? 0 },
    rotation,
  };
  if (ax !== undefined && ay !== undefined) {
    ctx.alignment = { x: ax, y: ay };
  }
  if (rawGroups.length > 0) ctx.rawGroups = rawGroups;
  return ctx;
};

const parseMtextContext: SubclassParser = (groups, base) => {
  let ix: number | undefined;
  let iy: number | undefined;
  let rotation = 0;
  let width: number | undefined;
  let attachment: number | undefined;
  const rawGroups: DxfGroup[] = [];
  for (const g of groups) {
    switch (g.code) {
      case 10: ix = parseFloatSafe(g.value); break;
      case 20: iy = parseFloatSafe(g.value); break;
      case 50: rotation = parseFloatSafe(g.value); break;
      case 41: width = parseFloatSafe(g.value); break;
      case 71: attachment = parseIntSafe(g.value); break;
      default: rawGroups.push(g);
    }
  }
  const ctx: AnnotScaleMtextContext = {
    ...base,
    kind: "mtext",
    insertion: { x: ix ?? 0, y: iy ?? 0 },
    rotation,
  };
  if (width !== undefined) ctx.width = width;
  if (attachment !== undefined) ctx.attachment = attachment;
  if (rawGroups.length > 0) ctx.rawGroups = rawGroups;
  return ctx;
};

const parseMtextAttributeContext: SubclassParser = (groups, base) => {
  let ix: number | undefined;
  let iy: number | undefined;
  let rotation = 0;
  let attribTag = "";
  const rawGroups: DxfGroup[] = [];
  for (const g of groups) {
    switch (g.code) {
      case 10: ix = parseFloatSafe(g.value); break;
      case 20: iy = parseFloatSafe(g.value); break;
      case 50: rotation = parseFloatSafe(g.value); break;
      case 2: attribTag = decodeDxfUnicode(g.value.trim()); break; // T9 PR-3 — attrib tag decode
      default: rawGroups.push(g);
    }
  }
  const ctx: AnnotScaleMtextAttributeContext = {
    ...base,
    kind: "mtext_attribute",
    insertion: { x: ix ?? 0, y: iy ?? 0 },
    rotation,
    attribTag,
  };
  if (rawGroups.length > 0) ctx.rawGroups = rawGroups;
  return ctx;
};

/**
 * INSERT vs ATTRIB share the same `AcDbBlkRefObjectContextData` subclass.
 * AutoCAD differentiates them at decode time by whether a tag (group 2)
 * is present (ATTRIB has it, INSERT does not). We emit different
 * discriminants so consumers can dispatch without re-checking shape.
 */
const parseBlkRefContext: SubclassParser = (groups, base) => {
  let ix: number | undefined;
  let iy: number | undefined;
  let scaleX = 1;
  let scaleY = 1;
  let rotation = 0;
  let attribTag: string | undefined;
  const rawGroups: DxfGroup[] = [];
  for (const g of groups) {
    switch (g.code) {
      case 10: ix = parseFloatSafe(g.value); break;
      case 20: iy = parseFloatSafe(g.value); break;
      case 41: scaleX = parseFloatSafe(g.value); break;
      case 42: scaleY = parseFloatSafe(g.value); break;
      case 50: rotation = parseFloatSafe(g.value); break;
      case 2: attribTag = decodeDxfUnicode(g.value.trim()); break; // T9 PR-3 — attrib tag decode
      default: rawGroups.push(g);
    }
  }
  if (attribTag !== undefined && attribTag !== "") {
    const ctx: AnnotScaleAttributeContext = {
      ...base,
      kind: "attribute",
      insertion: { x: ix ?? 0, y: iy ?? 0 },
      rotation,
      attribTag,
    };
    if (rawGroups.length > 0) ctx.rawGroups = rawGroups;
    return ctx;
  }
  const ctx: AnnotScaleBlockrefContext = {
    ...base,
    kind: "blockref",
    insertion: { x: ix ?? 0, y: iy ?? 0 },
    scaleX,
    scaleY,
    rotation,
  };
  if (rawGroups.length > 0) ctx.rawGroups = rawGroups;
  return ctx;
};

const parseHatchContext: SubclassParser = (groups, base) => {
  let patternScale = 1;
  let patternAngle = 0;
  const rawGroups: DxfGroup[] = [];
  for (const g of groups) {
    switch (g.code) {
      case 41: patternScale = parseFloatSafe(g.value); break;
      case 53: patternAngle = parseFloatSafe(g.value); break;
      default: rawGroups.push(g);
    }
  }
  const ctx: AnnotScaleHatchContext = {
    ...base,
    kind: "hatch",
    patternScale,
    patternAngle,
  };
  if (rawGroups.length > 0) ctx.rawGroups = rawGroups;
  return ctx;
};

/**
 * DIMENSION subtypes share the same group-code shape (repeated 10/20
 * pairs for definition points + optional 11/21 text position). The
 * subtype enum is captured from the concrete subclass marker so the
 * writer can re-emit the correct one.
 */
function parseDimensionContext(
  groups: DxfGroup[],
  base: AnnotScaleBase,
  subtype: AnnotScaleDimensionSubtype,
): AnnotScaleDimensionContext {
  const definitionPoints: AnnotScalePoint[] = [];
  let pendingX: number | undefined;
  let textPositionX: number | undefined;
  let textPosition: AnnotScalePoint | undefined;
  const rawGroups: DxfGroup[] = [];

  for (const g of groups) {
    switch (g.code) {
      case 10:
        pendingX = parseFloatSafe(g.value);
        break;
      case 20:
        if (pendingX !== undefined) {
          definitionPoints.push({ x: pendingX, y: parseFloatSafe(g.value) });
          pendingX = undefined;
        }
        break;
      case 11:
        textPositionX = parseFloatSafe(g.value);
        break;
      case 21:
        if (textPositionX !== undefined) {
          textPosition = { x: textPositionX, y: parseFloatSafe(g.value) };
          textPositionX = undefined;
        }
        break;
      default:
        rawGroups.push(g);
    }
  }

  const ctx: AnnotScaleDimensionContext = {
    ...base,
    kind: "dimension",
    subtype,
    definitionPoints,
  };
  if (textPosition) ctx.textPosition = textPosition;
  if (rawGroups.length > 0) ctx.rawGroups = rawGroups;
  return ctx;
}

const parseMleaderContext: SubclassParser = (groups, base) => {
  let textHeight: number | undefined;
  let arrowheadSize: number | undefined;
  let doglegLength: number | undefined;
  let landingGap: number | undefined;
  let textRotation: number | undefined;
  let textColor: string | undefined;
  let textWidth: number | undefined;
  const rawGroups: DxfGroup[] = [];
  for (const g of groups) {
    switch (g.code) {
      case 40: textHeight = parseFloatSafe(g.value); break;
      case 140: arrowheadSize = parseFloatSafe(g.value); break;
      case 41: doglegLength = parseFloatSafe(g.value); break;
      case 145: landingGap = parseFloatSafe(g.value); break;
      case 42: textRotation = parseFloatSafe(g.value); break;
      case 178: textColor = g.value.trim(); break;
      case 43: textWidth = parseFloatSafe(g.value); break;
      default: rawGroups.push(g);
    }
  }
  const ctx: AnnotScaleMleaderContext = {
    ...base,
    kind: "mleader",
  };
  if (textHeight !== undefined) ctx.textHeight = textHeight;
  if (arrowheadSize !== undefined) ctx.arrowheadSize = arrowheadSize;
  if (doglegLength !== undefined) ctx.doglegLength = doglegLength;
  if (landingGap !== undefined) ctx.landingGap = landingGap;
  if (textRotation !== undefined) ctx.textRotation = textRotation;
  if (textColor !== undefined) ctx.textColor = textColor;
  if (textWidth !== undefined) ctx.textWidth = textWidth;
  if (rawGroups.length > 0) ctx.rawGroups = rawGroups;
  return ctx;
};

const parseLeaderContext: SubclassParser = (groups, base) => {
  const vertices: AnnotScalePoint[] = [];
  let pendingX: number | undefined;
  const rawGroups: DxfGroup[] = [];
  for (const g of groups) {
    switch (g.code) {
      case 10:
        pendingX = parseFloatSafe(g.value);
        break;
      case 20:
        if (pendingX !== undefined) {
          vertices.push({ x: pendingX, y: parseFloatSafe(g.value) });
          pendingX = undefined;
        }
        break;
      default:
        rawGroups.push(g);
    }
  }
  const ctx: AnnotScaleLeaderContext = {
    ...base,
    kind: "leader",
    vertices,
  };
  if (rawGroups.length > 0) ctx.rawGroups = rawGroups;
  return ctx;
};

const parseToleranceContext: SubclassParser = (groups, base) => {
  let ix: number | undefined;
  let iy: number | undefined;
  let rotation = 0;
  const rawGroups: DxfGroup[] = [];
  for (const g of groups) {
    switch (g.code) {
      case 10: ix = parseFloatSafe(g.value); break;
      case 20: iy = parseFloatSafe(g.value); break;
      case 50: rotation = parseFloatSafe(g.value); break;
      default: rawGroups.push(g);
    }
  }
  const ctx: AnnotScaleToleranceContext = {
    ...base,
    kind: "tolerance",
    insertion: { x: ix ?? 0, y: iy ?? 0 },
    rotation,
  };
  if (rawGroups.length > 0) ctx.rawGroups = rawGroups;
  return ctx;
};

// Map dimension subclass marker → enum literal used in the union.
const DIMENSION_SUBCLASS_MAP: Record<string, AnnotScaleDimensionSubtype> = {
  [SUBCLASS_DIM_ALIGNED]: "aligned",
  [SUBCLASS_DIM_LINEAR]: "linear",
  [SUBCLASS_DIM_ANGULAR_3PT]: "angular",
  [SUBCLASS_DIM_ANGULAR_2L]: "angular",
  [SUBCLASS_DIM_DIAM]: "diametric",
  [SUBCLASS_DIM_RAD]: "radial",
  [SUBCLASS_DIM_ORD]: "ordinate",
};

// ── parseAcDbAnnotScaleObjectContextData (router) ──────────────────────

/**
 * Decode a context-data record given its concrete subclass and the body
 * groups. The `subclass` argument is the value of the second 100 marker
 * encountered (the first marker is `AcDbObjectContextData`); the body
 * groups are everything between that marker and the next 0 boundary.
 *
 * Returns null when the subclass is not in the recognised set — caller
 * should preserve the original groups verbatim through `rawObjectGroups`.
 */
export function parseAcDbAnnotScaleObjectContextData(
  groups: DxfGroup[],
  subclass: string,
  base: AnnotScaleBase,
): AnnotScaleContext | null {
  switch (subclass) {
    case SUBCLASS_TEXT:
      return parseTextContext(groups, base);
    case SUBCLASS_MTEXT:
      return parseMtextContext(groups, base);
    case SUBCLASS_MTEXT_ATTR:
      return parseMtextAttributeContext(groups, base);
    case SUBCLASS_BLKREF:
      return parseBlkRefContext(groups, base);
    case SUBCLASS_HATCH:
      return parseHatchContext(groups, base);
    case SUBCLASS_DIM:
      // Bare AcDbDimensionObjectContextData with no concrete sub-subclass
      // marker — default to "aligned" (the most common case in the wild)
      // and capture all groups in rawGroups so the writer can round-trip
      // verbatim.
      return parseDimensionContext(groups, base, "aligned");
    case SUBCLASS_DIM_ALIGNED:
    case SUBCLASS_DIM_LINEAR:
    case SUBCLASS_DIM_ANGULAR_3PT:
    case SUBCLASS_DIM_ANGULAR_2L:
    case SUBCLASS_DIM_DIAM:
    case SUBCLASS_DIM_RAD:
    case SUBCLASS_DIM_ORD: {
      const subtype = DIMENSION_SUBCLASS_MAP[subclass] ?? "aligned";
      return parseDimensionContext(groups, base, subtype);
    }
    case SUBCLASS_MLEADER:
      return parseMleaderContext(groups, base);
    case SUBCLASS_LEADER:
      return parseLeaderContext(groups, base);
    case SUBCLASS_FCF:
      return parseToleranceContext(groups, base);
    default:
      return null;
  }
}

// ── dispatchObjectsAnnotativeRecord ────────────────────────────────────

/**
 * Top-level dispatcher invoked by `parseObjectsSection` for any record
 * whose 100-subclass marker chain matches one of the annotative
 * context-data classes. Walks the record once to:
 *
 *  1. Capture the handle (group 5), ownerHandle (group 330), and the
 *     scale-handle FK (group 340).
 *  2. Locate the concrete subclass marker — the second 100 marker that
 *     is not `AcDbObjectContextData`.
 *  3. Slice the body groups (everything after that concrete marker up
 *     to but not including the next 0 boundary) and hand them to the
 *     per-subclass parser.
 *
 * Returns `null` for any record that, on close inspection, isn't
 * actually a context-data record (e.g. the type matched the predicate
 * loosely but the marker chain is incomplete).
 *
 * @param type      — DXF group 0 value of the record.
 * @param groups    — full group list of the record (group 0 included).
 */
export function dispatchObjectsAnnotativeRecord(
  type: string,
  groups: DxfGroup[],
): ParsedAnnotativeRecord | null {
  let handle = "";
  let ownerHandle = "";
  let scaleHandle = "";
  let flags = 0;
  let defaultFlag: number | undefined;
  let concreteSubclass: string | null = null;
  let concreteSubclassIndex = -1;
  // The intermediate `AcDbAnnotScaleObjectContextData` marker, if seen.
  // When the concrete marker is missing we use this as a fallback anchor
  // for body-slicing (АБИ1.dxf and many real-world AutoCAD outputs only
  // emit the intermediate base + base markers; concrete kind is encoded
  // in group 0 — see TYPE_TO_SUBCLASS).
  let annotBaseIndex = -1;

  // First pass — capture envelope fields and locate concrete subclass.
  for (let i = 0; i < groups.length; i++) {
    const g = groups[i]!;
    if (g.code === 5 && handle === "") {
      handle = g.value.trim();
      continue;
    }
    if (g.code === 330 && ownerHandle === "") {
      ownerHandle = g.value.trim();
      continue;
    }
    if (g.code === 340 && scaleHandle === "") {
      scaleHandle = g.value.trim();
      continue;
    }
    if (g.code === 70 && concreteSubclass === null && annotBaseIndex < 0) {
      // First 70 we see (before any annotative marker) is the
      // `AcDbObjectContextData` flags (per spec it follows the base
      // subclass marker). Capture once.
      flags = parseIntSafe(g.value);
      continue;
    }
    if (
      g.code === 290 &&
      defaultFlag === undefined &&
      concreteSubclass === null &&
      annotBaseIndex < 0
    ) {
      // DXF group 290 — `AcDbObjectContextData` "is-default-context-data" bool
      // (Autodesk R2018). It sits in the base subclass right after group 70 and
      // BEFORE the `AcDbAnnotScaleObjectContextData` marker, so it is captured
      // here (envelope, not body). Guarded to the base-subclass region (mirrors
      // the 70 branch) so a 290 that appears inside a concrete subclass body is
      // left for `rawGroups` round-trip instead.
      defaultFlag = parseIntSafe(g.value);
      continue;
    }
    if (g.code === 100) {
      const marker = g.value.trim();
      if (marker === SUBCLASS_BASE) continue;
      if (marker === SUBCLASS_ANNOT_BASE) {
        if (annotBaseIndex < 0) annotBaseIndex = i;
        continue;
      }
      if (ALL_CONTEXT_DATA_MARKERS.has(marker)) {
        concreteSubclass = marker;
        concreteSubclassIndex = i;
        // Don't break — still need to consume any remaining envelope
        // fields encountered after the marker (rare but possible).
      }
    }
  }

  // Fallback: derive concrete subclass from the registered DXF class
  // name (group 0) when only the intermediate marker is present. This is
  // the path taken for AutoCAD-authored files that omit the concrete
  // `AcDb<Kind>ObjectContextData` marker. The body slice anchors at the
  // intermediate marker instead.
  if (concreteSubclass === null && annotBaseIndex >= 0 && type) {
    const mapped = TYPE_TO_SUBCLASS[type.trim().toUpperCase()];
    if (mapped) {
      concreteSubclass = mapped;
      concreteSubclassIndex = annotBaseIndex;
    }
  }

  if (concreteSubclass === null || concreteSubclassIndex < 0) {
    return null;
  }

  // Slice the body — everything strictly after the concrete subclass
  // marker, excluding the envelope fields (5/330/340) which we've
  // already consumed.
  const body: DxfGroup[] = [];
  for (let i = concreteSubclassIndex + 1; i < groups.length; i++) {
    const g = groups[i]!;
    if (g.code === 5 || g.code === 330 || g.code === 340) continue;
    // Stop at any further 100 marker — protects against records that
    // chain multiple unrelated subclasses (rare but seen in vendor
    // extensions).
    if (g.code === 100) break;
    body.push(g);
  }

  const base: AnnotScaleBase = {
    scaleName: "",     // resolved second pass
    scaleFactor: 1,    // resolved second pass
    flags,
    ...(defaultFlag !== undefined ? { defaultFlag } : {}),
    ...(ownerHandle ? { ownerHandle } : {}),
  };

  const context = parseAcDbAnnotScaleObjectContextData(
    body,
    concreteSubclass,
    base,
  );
  if (!context) return null;

  return {
    kind: "context-data",
    subclass: concreteSubclass,
    handle,
    ownerHandle,
    scaleHandle,
    context,
  };
}

// ── AcadAnnotativeDecomposition (BLOCK_RECORD XData) ──────────────────

/**
 * Map of `scaleName → anonymousBlockHandle` extracted from the
 * `AcadAnnotativeDecomposition` XData app-name on a BLOCK_RECORD. The
 * AutoCAD persistence shape (per ObjectARX reference) is:
 *
 * ```
 *   1001 AcadAnnotativeDecomposition
 *   1000 <scaleName>           ← string (e.g. "1:50")
 *   1005 <anonymousBlockHandle> ← handle (e.g. "*A_127")
 *   1000 <scaleName>           ← repeats per scale
 *   1005 <anonymousBlockHandle>
 *   …
 * ```
 *
 * Returns an empty record when the XData entry is absent or malformed
 * (empty pairs are skipped silently — no entries dropped if the file
 * happens to be authored by a non-AutoCAD writer).
 *
 * @param xdataGroups groups inside an XData entry whose appName is
 *   `AcadAnnotativeDecomposition` (i.e. WITHOUT the leading `1001`).
 */
export function parseAcadAnnotativeDecomposition(
  xdataGroups: ReadonlyArray<DxfGroup>,
): Record<string, string> {
  const result: Record<string, string> = {};
  let pendingName: string | null = null;
  for (const g of xdataGroups) {
    if (g.code === 1000) {
      // T9 PR-3 — the 1000 scale NAME is a string-content XDATA code the writer
      // chokepoint escapes; decode so it keys the map under the real characters.
      // Idempotent for the BLOCK_RECORD path whose XDATA collector already
      // decoded 1000 (decodeDxfUnicode returns its input when no `\U+` remains).
      pendingName = decodeDxfUnicode(g.value);
      continue;
    }
    if (g.code === 1005 && pendingName !== null) {
      const handle = g.value.trim();
      if (handle !== "") {
        result[pendingName] = handle;
      }
      pendingName = null;
    }
  }
  return result;
}

/**
 * Convenience: walk a BLOCK_RECORD's XData list and return the
 * `AcadAnnotativeDecomposition` map (empty if absent). Used by the
 * bridge layer when building `BlockRecord.annotativeDecompositions`.
 *
 * The XData entry shape mirrors `DxfXDataEntry` from `./types` —
 * structurally `{ appName: string, groups: DxfGroup[] }`. Defined as
 * a structural type here to keep this module independent of the wider
 * type surface.
 */
export function parseAcadAnnotativeDecompositionFromXData(
  xdata: ReadonlyArray<{ appName: string; groups: DxfGroup[] }>,
): Record<string, string> {
  for (const entry of xdata) {
    if (entry.appName === "AcadAnnotativeDecomposition") {
      return parseAcadAnnotativeDecomposition(entry.groups);
    }
  }
  return {};
}
